/**
 * Lector de código de barras en modo teclado (HID).
 *
 * El lector "teclea" el código y manda Enter (o Tab), así que el navegador no lo
 * distingue de una persona: se reconoce por la velocidad. Un lector deja pocos
 * milisegundos entre tecla y tecla; una persona, más de 80.
 */

import { useEffect, useRef } from 'react'

export const SCAN_MIN_LENGTH = 4
export const SCAN_MAX_GAP_MS = 50

type KeyInput = Pick<KeyboardEvent, 'key' | 'timeStamp' | 'ctrlKey' | 'metaKey' | 'altKey' | 'target'>

export interface DetectedScan {
  code: string
  target: EventTarget | null
  /** Lo que tenía el campo antes de que el lector empezara a escribir en él. */
  valueBefore: string | null
}

function readValue(target: EventTarget | null): string | null {
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.value
  }
  return null
}

export function createScanDetector(minLength = SCAN_MIN_LENGTH, maxGapMs = SCAN_MAX_GAP_MS) {
  let buffer = ''
  let lastAt = 0
  let target: EventTarget | null = null
  let valueBefore: string | null = null

  const reset = () => {
    buffer = ''
    target = null
    valueBefore = null
  }

  return {
    /** Devuelve el escaneo cuando la tecla lo cierra; si no, null. */
    feed(event: KeyInput): DetectedScan | null {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        reset()
        return null
      }
      // El lector manda Shift para las mayúsculas: no corta la ráfaga.
      if (event.key === 'Shift' || event.key === 'CapsLock') return null

      if (buffer && event.timeStamp - lastAt > maxGapMs) reset()

      if (event.key === 'Enter' || event.key === 'Tab') {
        const scan = buffer.length >= minLength ? { code: buffer, target, valueBefore } : null
        reset()
        return scan
      }
      if (event.key.length === 1) {
        if (!buffer) {
          target = event.target
          valueBefore = readValue(event.target)
        }
        buffer += event.key
        lastAt = event.timeStamp
        return null
      }
      reset()
      return null
    },
  }
}

/** Devuelve el campo a su valor previo, avisándole a React del cambio. */
function restoreValue(target: EventTarget | null, value: string | null) {
  if (value === null) return
  const proto =
    target instanceof HTMLInputElement
      ? HTMLInputElement.prototype
      : target instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : null
  const setter = proto && Object.getOwnPropertyDescriptor(proto, 'value')?.set
  if (!setter || !(target instanceof HTMLElement)) return
  setter.call(target, value)
  target.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * Captura los escaneos en toda la página, tenga el foco quien lo tenga: lo que
 * el lector tecleó en otro campo se deshace, y el Enter final no llega a
 * enviar un formulario ni a apretar el botón enfocado. `target` es el campo
 * donde cayó, para que un buscador pueda reemplazar su texto por el código.
 */
export function useBarcodeScanner(
  onScan: (code: string, target: EventTarget | null) => void,
  enabled = true
) {
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  useEffect(() => {
    if (!enabled) return
    const detector = createScanDetector()
    const handler = (event: KeyboardEvent) => {
      const scan = detector.feed(event)
      if (!scan) return
      event.preventDefault()
      event.stopPropagation()
      restoreValue(scan.target, scan.valueBefore)
      onScanRef.current(scan.code, scan.target)
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [enabled])
}

let audioContext: AudioContext | null = null

/**
 * Prepara el audio. Hay que llamarlo dentro del evento de teclado: el navegador
 * solo deja arrancar sonido como respuesta directa al usuario, y el aviso de
 * error suena después de esperar al servidor.
 */
export function primeScanAudio() {
  try {
    audioContext ??= new AudioContext()
    if (audioContext.state === 'suspended') void audioContext.resume()
  } catch {
    audioContext = null
  }
}

/** Tonos que empiezan en `offsets` segundos. Sin audio no suena y no falla. */
function beep(type: OscillatorType, frequency: number, duration: number, offsets = [0]) {
  const ctx = audioContext
  if (!ctx) return
  try {
    for (const offset of offsets) {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = type
      oscillator.frequency.value = frequency
      gain.gain.value = 0.08
      oscillator.connect(gain).connect(ctx.destination)
      const start = ctx.currentTime + offset
      oscillator.start(start)
      oscillator.stop(start + duration)
    }
  } catch {
    // El aviso en pantalla alcanza.
  }
}

/**
 * Pitido corto de lectura para la cámara, que a diferencia del lector no pita
 * sola. La vibración ayuda donde el sonido no se oye.
 */
export function playScanReadSound() {
  navigator.vibrate?.(60)
  beep('sine', 1800, 0.08)
}

/**
 * Tono grave doble para "no se agregó". El acierto no suena: el lector ya pita
 * al leer, y un segundo pitido igual se confunde con el primero.
 */
export function playScanErrorSound() {
  beep('square', 220, 0.12, [0, 0.18])
}
